// Global
import React from 'react';

// Lib
import { UserProfile } from 'src/.generated/UserProfile/Login.model';
import Login from './Login';
import RichTextWrapper from 'components/helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import PlainTextWrapper, {
  usePlainTextWrapper,
} from 'components/helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import LinkWrapper, {
  useLinkWrapper,
} from 'components/helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import styles from './Login.module.scss';
import { useSiteSettings } from 'lib/hooks/useSiteSettings';
import { Data } from '@generated/Foundation.GMI.model';
import { useDisabledFeatureFlags } from 'lib/hooks/useDisabledFeatureFlags';

type LoginSitecoreProps = UserProfile.Login.Login_Component;

export const Default = ({ fields }: LoginSitecoreProps): JSX.Element => {
  const siteSettings = useSiteSettings();

  const loginRegistrationSettings =
    siteSettings?.loginRegistrationSettings as Data.Settings.LoginAndRegistrationSettings;
  const disabledFeatures = useDisabledFeatureFlags();
  const skipCaptcha = disabledFeatures.CAPTCHA !== false;

  const enableRememberMe = loginRegistrationSettings?.fields?.enableRememberMe?.value || false;
  const enableGoogleLogin = !loginRegistrationSettings?.fields?.hideSocialLoginWithGoogle?.value;
  const enableFacebookLogin =
    !loginRegistrationSettings?.fields?.hideSocialLoginWithFacebook?.value;

  const {
    description,
    emailLabel,
    emailPlaceholderText,
    forgotPasswordLink,
    headline,
    loginButtonText,
    passwordLabel,
    passwordPlaceholderText,
    remembermeText,
    signupLink,
    // Social Connect fields (from _SocialConnect template)
    alreadyMemberHeading,
    alreadyMemberDescription,
    verifyEmailLabel,
    verifyEmailPlaceholder,
    socialConnectSubmitText,
    cancelCTA,
  } = fields || {};

  // Social Connect field wrappers
  const alreadyMemberHeadingNode = usePlainTextWrapper({ field: alreadyMemberHeading });
  const alreadyMemberDescriptionNode = usePlainTextWrapper({ field: alreadyMemberDescription });
  const verifyEmailLabelNode = usePlainTextWrapper({ field: verifyEmailLabel });
  const socialConnectSubmitTextNode = usePlainTextWrapper({ field: socialConnectSubmitText });
  const socialConnectCancelLink = useLinkWrapper({
    field: cancelCTA,
    ctaStyle: 'link',
    ctaVariant: 'brand',
  });

  if (!fields) {
    return <></>;
  }

  return (
    <Login
      skipCaptcha={skipCaptcha}
      enableRememberMe={enableRememberMe}
      enableGoogleLogin={enableGoogleLogin}
      enableFacebookLogin={enableFacebookLogin}
      description={<RichTextWrapper field={description} className={styles.subheading} />}
      emailLabel={<PlainTextWrapper field={emailLabel} />}
      emailPlaceholderText={emailPlaceholderText?.value || ''}
      forgotPasswordLink={
        <LinkWrapper
          field={forgotPasswordLink}
          ctaStyle="link"
          ctaVariant="brand"
          className={styles.forgotPassword}
        />
      }
      headline={<PlainTextWrapper field={headline} />}
      loginButtonText={<PlainTextWrapper field={loginButtonText} />}
      passwordLabel={<PlainTextWrapper field={passwordLabel} />}
      passwordPlaceholderText={passwordPlaceholderText?.value || ''}
      remembermeText={<PlainTextWrapper field={remembermeText} />}
      signupLink={
        <LinkWrapper
          field={signupLink}
          ctaStyle="link"
          ctaVariant="brand"
          className={styles.signupLink}
        />
      }
      alreadyMemberHeading={alreadyMemberHeadingNode}
      alreadyMemberDescription={alreadyMemberDescriptionNode}
      verifyEmailLabel={verifyEmailLabelNode}
      verifyEmailPlaceholder={verifyEmailPlaceholder?.value || ''}
      socialConnectSubmitText={socialConnectSubmitTextNode}
      socialConnectCancelLink={socialConnectCancelLink}
    />
  );
};
